import React from 'react';
import { Link } from 'react-router-dom';
import GuideLayout, { SectionCTA } from '../../components/GuideLayout';

const s = {
  hero: { marginBottom: 48 },
  tag: { fontSize: 12, fontWeight: 700, color: '#2563eb', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 12 },
  h1: { fontSize: 36, fontWeight: 800, color: '#0f172a', lineHeight: 1.2, marginBottom: 16, letterSpacing: '-0.5px' },
  intro: { fontSize: 17, color: '#475569', lineHeight: 1.7, maxWidth: 600, marginBottom: 32 },
  stats: { display: 'flex', gap: 32, flexWrap: 'wrap', marginBottom: 0 },
  stat: { textAlign: 'left' },
  statNum: { fontSize: 28, fontWeight: 800, color: '#1e40af', lineHeight: 1 },
  statLabel: { fontSize: 13, color: '#64748b', marginTop: 4 },
  divider: { border: 'none', borderTop: '1px solid #e2e8f0', margin: '40px 0' },
  sectionTitle: { fontSize: 18, fontWeight: 700, color: '#0f172a', marginBottom: 20 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '24px', textDecoration: 'none', display: 'block',
    transition: 'box-shadow 0.15s',
  },
  cardNum: { fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 10 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 8, lineHeight: 1.3 },
  cardDesc: { fontSize: 14, color: '#64748b', lineHeight: 1.6 },
  cardLink: { fontSize: 13, color: '#2563eb', fontWeight: 600, marginTop: 14, display: 'block' },
};

const sections = [
  {
    path: '/guide/what-is-a-tender',
    title: 'What is a public tender?',
    desc: 'Learn what public procurement is, who publishes contracts, what types of work are tendered, and why governments are legally required to use this process.',
  },
  {
    path: '/guide/who-can-bid',
    title: 'Who can bid?',
    desc: 'Any registered business can apply — including SMEs. Understand the eligibility requirements, exclusion grounds, and how consortium bids work.',
  },
  {
    path: '/guide/bidding-process',
    title: 'The bidding process',
    desc: 'A step-by-step walkthrough from the moment a tender is published to contract award — including submission, evaluation, and the standstill period.',
  },
  {
    path: '/guide/winning-a-bid',
    title: 'What makes a winning bid?',
    desc: 'How buyers score submissions, what MEAT means, the typical split between price and quality, and the most common mistakes that cost companies contracts.',
  },
  {
    path: '/guide/eu-rules',
    title: 'EU procurement rules',
    desc: 'The thresholds that trigger EU-wide publication, the main EU directives, framework agreements, and what rights you have as a cross-border bidder.',
  },
  {
    path: '/guide/glossary',
    title: 'Glossary',
    desc: 'Plain-English definitions for CPV codes, OJEU, MEAT, ITT, PQQ, standstill period, contracting authority, and ~20 other terms you\'ll encounter.',
  },
];

export default function GuideIndex() {
  return (
    <GuideLayout>
      <div style={s.hero}>
        <div style={s.tag}>Tender Guide</div>
        <h1 style={s.h1}>Everything you need to know about EU public tenders</h1>
        <p style={s.intro}>
          EU public procurement is one of the largest contract markets in the world — but most companies never bid because the process seems opaque. This guide explains how it works, step by step.
        </p>
        <div style={s.stats}>
          <div style={s.stat}>
            <div style={s.statNum}>&euro;2 trillion</div>
            <div style={s.statLabel}>spent on public contracts across the EU each year</div>
          </div>
          <div style={s.stat}>
            <div style={s.statNum}>750,000+</div>
            <div style={s.statLabel}>contract notices published annually on the EU platform</div>
          </div>
          <div style={s.stat}>
            <div style={s.statNum}>14%</div>
            <div style={s.statLabel}>of EU GDP flows through public procurement</div>
          </div>
        </div>
      </div>

      <hr style={s.divider} />

      <div style={s.sectionTitle}>Six sections — start anywhere</div>
      <div style={s.grid}>
        {sections.map((sec, i) => (
          <Link key={sec.path} to={sec.path} style={s.card}>
            <div style={s.cardNum}>{String(i + 1).padStart(2, '0')}</div>
            <div style={s.cardTitle}>{sec.title}</div>
            <div style={s.cardDesc}>{sec.desc}</div>
            <span style={s.cardLink}>Read section &rarr;</span>
          </Link>
        ))}
      </div>

      <SectionCTA />
    </GuideLayout>
  );
}
